import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError } from 'react-router';

import { ErrorState } from '@/components/states/ErrorState';

/**
 * The router's `errorElement` — the last thing between a thrown render and the user.
 *
 * Without one, react-router draws its own page: *"Unexpected Application Error! 404 Not Found · 💿
 * Hey developer 👋 …"*, with the stack in development. That is addressed to us and not to the person
 * using the product, and §8 is explicit — plain language and a Retry, **never a raw status code,
 * never a stack trace**. The error is still worth reading, so in development it goes to the console
 * where it belongs, and never onto the screen.
 *
 * Retry is a navigation rather than a reload: react-router clears the error boundary on navigating,
 * and `window.location.reload()` would throw away everything cached for the sake of one broken
 * screen.
 */
export default function RouteError() {
  const error = useRouteError();
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  if (import.meta.env.DEV) {
    console.error('Unhandled route error:', error);
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-paper-bg px-8">
      <div className="w-full max-w-sm">
        {/*
          `headingLevel={1}`: this state **is** the page — the shell and its screen title are gone
          with the render that threw — so its title is the document's one `h1`. Without it the whole
          document had no heading, and a screen reader arriving here had nothing to orient by (§9).
        */}
        <ErrorState
          headingLevel={1}
          message={t('routeError')}
          onRetry={() => void navigate('/', { replace: true })}
        />
      </div>
    </main>
  );
}
